//This util will eventually be moved into the Device Feature "utils" folder
// Features / Devices / utils / deviceGroupRules
import { groupRules as RULES } from './DeviceGroupRules.js';

export function defineGroupType(selectedIds, deviceList, groupType = null) {
  if (selectedIds == null) return null;
  if (!Array.isArray(deviceList)) return null;

  //Get the devices that are part of the group
  const filteredDevices = deviceList.filter((device) => selectedIds.includes(device.id));
  if (filteredDevices.length === 0) return null;

  const deviceKeys = Object.keys(RULES.devices || {});

  const foundDevices = filteredDevices.reduce((acc, device) => {
    if (!device) return acc;
    const raw = device.device_type || null;
    const normalized = normalizeDeviceType(raw);
    if (normalized && deviceKeys.includes(normalized)) {
      acc.push({ device, normalized });
    }
    return acc;
  }, []);

  //Take first device device_type as the group type if none selected
  const foundDeviceType =
    foundDevices && foundDevices.length > 0 ? foundDevices[0].device.device_type : null;

  // if group has no type selected, return the found deviceType (string or null)
  if (!groupType) {
    return foundDeviceType;
  } else {
    // Has existing group type — verify it still exists in the normalized list, else clear it.
    const existingType = groupType || null;
    if (existingType && typeof existingType === 'string') {
      const normalizedExisting = normalizeDeviceType(existingType);
      if (normalizedExisting) {
        const match = foundDevices.find((f) => f.normalized === normalizedExisting);
        if (match) {
          // return the raw device_type from the matching device
          return match.device.device_type ?? null;
        }
      }
      // existing type no longer present -> pick the next available device type if present
      if (foundDevices && foundDevices.length > 0) {
        return foundDevices[0].device.device_type ?? null;
      }
      // nothing to fall back to -> clear it
      return null;
    }
  }

  return null;
}
// trim, lowercase, remove slashes but keep underscores
function normalizeDeviceType(deviceType) {
  if (typeof deviceType !== 'string') return null;
  // trim, lowercase, remove slashes and all whitespace but keep underscores
  const normalized = deviceType.trim().toLowerCase().replace(/\//g, '').replace(/\s+/g, '');
  return normalized;
}

// Match `filterSelectionByType(selectedIds, selectedType, connectedDevices = [])`
// Returns an array of selectedIds that are allowed under RULES and the provided selectedType.
export function deviceListforGroup(selectedIds, selectedType, connectedDevices = []) {
  if (!connectedDevices || !Array.isArray(connectedDevices)) return [];
  if (!Array.isArray(selectedIds)) return [];

  // Apply RULES-based filtering for device-category types, but always allow
  // controllers and aux devices (they are managed outside of device mix rules).
  const deviceList = selectedIds.filter((id) => {
    const device = connectedDevices.find((d) => d.id === id);
    if (!device) return false;

    // Determine category using RULES.typeMap (normalized values)
    const rawType = device.device_type || '';
    const normalizedRawType = normalizeDeviceType(rawType);
    const typeMap = RULES.typeMap || {};
    const isController = (typeMap.controllers || []).includes(normalizedRawType);
    const isDevice = (typeMap.devices || []).includes(normalizedRawType);
    const isAux = (typeMap.aux_devices || []).includes(normalizedRawType);

    // controllers and aux devices are always allowed
    if (isController || isAux) return true;

    // If this type is not explicitly listed in RULES (unknown type), allow it.
    if (!isDevice) return true;

    // For regular 'devices' category, apply compatibility rules
    return isDeviceAllowed(selectedType, device);
  });

  return deviceList;
}

// Helper: determine whether a single device is allowed for a given group type
// Returns boolean. Logs an error and returns false on unexpected input.
function isDeviceAllowed(groupType, device) {
  try {
    if (!device || typeof device.device_type !== 'string') {
      return false;
    }

    const deviceTypeNorm = normalizeDeviceType(device.device_type);
    if (!deviceTypeNorm) {
      return false;
    }
    // Reuse the exported helper that evaluates a device-type string
    // against the selected group type to keep logic centralized.
    const allowed = isDeviceTypeAllowed(device.device_type, groupType);
    return allowed;
  } catch (e) {
    return false;
  }
}

// Allow or block a device type string based on the current exclusive selection
export function isDeviceTypeAllowed(deviceType, selectedType = '') {
  try {
    if (typeof deviceType !== 'string') return false;

    const deviceTypeNorm = normalizeDeviceType(deviceType);
    if (!deviceTypeNorm) return false;

    const deviceKeys = Object.keys(RULES.devices || {});
    // unknown device types -> permissive
    if (!deviceKeys.includes(deviceTypeNorm)) return true;

    if (!selectedType) return true;

    const normalizedGroup = normalizeDeviceType(selectedType);
    if (!normalizedGroup) return false;

    if (normalizedGroup === deviceTypeNorm) return true;

    const mixRules = RULES.deviceMixRules || {};
    const ruleVal = (v) => (v == null ? null : String(v).trim().toUpperCase());
    const pair1 = `${normalizedGroup}:${deviceTypeNorm}`;
    const pair2 = `${deviceTypeNorm}:${normalizedGroup}`;
    const rule1 = ruleVal(mixRules[pair1]);
    const rule2 = ruleVal(mixRules[pair2]);

    if (rule1 === 'NO' || rule2 === 'NO') return false;
    return true;
  } catch (e) {
    return false;
  }
}

//This function will return a list of device groups that are compatible with the provided device type
export function groupListforDevice(deviceType, groups = []) {
  if (typeof deviceType !== 'string') return [];
  if (!Array.isArray(groups)) return [];

  const groupListforDevice = groups.filter((group) => {
    const groupType = group.type || null;
    if (!groupType) return true; // Groups with no type are always allowed
    return isDeviceTypeAllowed(deviceType, groupType);
  });

  return groupListforDevice;
}
